// What makes two listings the same tune (Oct 2026): the rules the library
// build merges versions by (src/music/libraryStats.ts), shared with the
// browser, which matches a tune you opened, saved or filed to its library
// entry the same way. Each rule was earned by a real case; see
// mergeLibraryTunes.

const lettersOnly = (value: string) =>
  value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

const dropArticle = (value: string) => value.replace(/^the /, '').replace(/ the$/, '');

/** Lower-case letters and digits of any script, accents dropped, everything
 * else a single space. */
export function normalizeName(value: string): string {
  return lettersOnly(value);
}

/**
 * The key versions of one tune share across sources: every script's letters
 * and digits (an a–z-only key empties a Cyrillic title), no parenthetical
 * ("Indiana (Back Home Again In)" is the Real Book's "Indiana"), no leading or
 * trailing article ("Girl From Ipanema, The"). Empty when the title has no
 * letters or digits at all.
 */
export function libraryTitleKey(title: string): string {
  return dropArticle(lettersOnly(title.replace(/\(.*?\)/gs, ' ')));
}

/** A parenthesis naming a version of a chart rather than another tune:
 * "(Page 2 - Solos)", "(Original)", "(Coltrane Changes)", "(Miles Davis
 * version)", "(duo)". */
const CHART_VARIANT = /\([^)]*\b(?:page|original|changes|version|duo|solos?)\b[^)]*\)/gis;

/** The whole title, parentheses kept except a chart variant's: what tells
 * "Milestones (Old)" from "Milestones (New)" inside one source, while
 * "Desafinado (Original)" stays Desafinado. */
export function fullTitleKey(title: string): string {
  return dropArticle(lettersOnly(title.replace(CHART_VARIANT, ' ')));
}

/** A credit that names no writer agrees with any credit. */
const UNATTRIBUTED = new Set(['traditional', 'trad', 'anonymous', 'unknown', 'folk']);

/**
 * Name words of a credit, three letters or longer, plus each pair of
 * neighbours run together: "Raye-DePaul" and "Gene de Paul / Don Raye" share
 * "raye", "A. C. Jobim" carries "jobim", and "J. Mc Hugh" carries "mchugh".
 * Empty for a credit that names nobody, which then agrees with anything.
 */
export function creditWords(composer: string | undefined): Set<string> {
  const tokens = lettersOnly(composer ?? '')
    .split(' ')
    .filter(Boolean);
  if (tokens.some((token) => UNATTRIBUTED.has(token))) return new Set();
  const words = new Set(tokens.filter((word) => word.length >= 3 && word !== 'and'));
  for (let i = 1; i < tokens.length; i++) words.add(tokens[i - 1] + tokens[i]);
  return words;
}

/** One substitution, insertion or deletion apart: a misspelt surname
 * ("Handerson") still names its writer ("Henderson"). */
function nearlyEqual(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else {
      i++;
      j++;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}

export function composersAgree(a: Set<string>, b: Set<string>): boolean {
  if (a.size === 0 || b.size === 0) return true;
  for (const word of a) {
    if (b.has(word)) return true;
    if (word.length >= 6)
      for (const other of b) if (other.length >= 6 && nearlyEqual(word, other)) return true;
  }
  return false;
}

/** What `sameTune` compares, worked out once per listing. */
export interface TuneKeys {
  key: string;
  full: string;
  words: Set<string>;
}

export function tuneKeys(tune: { title: string; composer?: string }): TuneKeys {
  return {
    key: libraryTitleKey(tune.title),
    full: fullTitleKey(tune.title),
    words: creditWords(tune.composer),
  };
}

/** `sameTune` over keys already worked out. */
export function sameTuneKeys(a: TuneKeys, b: TuneKeys): boolean {
  if (!a.key || a.key !== b.key) return false;
  // Two different parentheses name two tunes ("Milestones (Old)" and
  // "Milestones (New)"); one listing without it is the same tune ("Indiana"
  // and "Indiana (Back Home Again In)").
  if (a.full !== b.full && a.full !== a.key && b.full !== b.key) return false;
  return composersAgree(a.words, b.words);
}

/**
 * Whether two listings name one tune, by the rules above: one title key,
 * credits that agree, and no two different parentheses. For tunes known from
 * different places, like a Top pick and the version you opened.
 */
export function sameTune(
  a: { title: string; composer?: string },
  b: { title: string; composer?: string },
): boolean {
  return sameTuneKeys(tuneKeys(a), tuneKeys(b));
}

/** Credits split into writers, each normalised: "Miles Davis, Bill Evans"
 * counts two composers and belongs to both. */
export function composerCredits(composer: string | undefined): string[] {
  return (composer ?? '')
    .split(/[,&/]|\band\b/i)
    .map((name) => lettersOnly(name))
    .filter(Boolean);
}

/**
 * A finder over a list of tunes: given a title and credit, the index of the
 * listed tune it names, or null. A title only one listing carries is that
 * tune whatever the credit says (a saved copy may carry none); where several
 * share it, the credit decides, and a credit agreeing with none of them
 * matches nothing rather than guessing.
 */
export function libraryTuneMatcher(
  tunes: readonly { title: string; composer?: string }[],
): (title: string, composer?: string) => number | null {
  const byKey = new Map<string, number[]>();
  tunes.forEach((tune, index) => {
    const key = libraryTitleKey(tune.title);
    if (key) byKey.set(key, [...(byKey.get(key) ?? []), index]);
  });
  return (title, composer) => {
    const candidates = byKey.get(libraryTitleKey(title));
    if (!candidates) return null;
    if (candidates.length === 1) return candidates[0];
    const full = fullTitleKey(title);
    const sameTitle = candidates.filter((index) => fullTitleKey(tunes[index].title) === full);
    const words = creditWords(composer);
    const pool = sameTitle.length ? sameTitle : candidates;
    return pool.find((index) => composersAgree(words, creditWords(tunes[index].composer))) ?? null;
  };
}
